import dayjs from 'dayjs';
import 'dayjs/locale/en';
import 'dayjs/locale/en-gb';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isBetween from 'dayjs/plugin/isBetween';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isoWeek from 'dayjs/plugin/isoWeek';
import localeData from 'dayjs/plugin/localeData';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import weekYear from 'dayjs/plugin/weekYear';
import weekday from 'dayjs/plugin/weekday';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);
dayjs.extend(advancedFormat);
dayjs.extend(isBetween);
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);
dayjs.extend(isoWeek);
dayjs.extend(weekOfYear);
// weekday + localeData + weekYear are required by the antd/rc-picker dayjs
// pickers (see components/fields/dayjsPickers.ts): rc-picker calls
// `value.weekday()` / `value.localeData()` / `value.weekYear()` on the *value
// objects the app passes in*. Those objects come from THIS dayjs copy, so the
// plugins must be extended here — rc-picker only extends its own nested copy.
dayjs.extend(weekday);
dayjs.extend(localeData);
dayjs.extend(weekYear);

// Match antd's historical default (en-US, Sunday-first). The one place that
// needs Monday-first weeks (Dashboard RangePicker) uses the 'en-gb' locale
// locally; both locales are registered above.
dayjs.locale('en');

export { dayjs };
export type Dayjs = dayjs.Dayjs;
export type DateInput = dayjs.ConfigType;

/** date-fns `Interval`: a `{ start, end }` date range. */
export type Interval = { start: Date | number; end: Date | number };

export const toDayjs = (value: unknown): Dayjs | null => {
  if (!value) {
    return null;
  }

  const date = dayjs(value as DateInput);

  return date.isValid() ? date : null;
};

/**
 * Inclusive list of every calendar day between `start` and `end`.
 * Mirrors date-fns `eachDayOfInterval`, except it returns `[]` for an invalid
 * or inverted interval (start after end, or a non-parseable bound) instead of
 * throwing a RangeError — a deliberate, friendlier contract.
 */
export const eachDayOfInterval = ({
  start,
  end,
}: {
  start: DateInput;
  end: DateInput;
}): Date[] => {
  const startDay = dayjs(start).startOf('day');
  const endDay = dayjs(end).startOf('day');

  if (!startDay.isValid() || !endDay.isValid() || startDay.isAfter(endDay)) {
    return [];
  }

  const days: Date[] = [];
  let cursor = startDay;

  while (cursor.isSameOrBefore(endDay, 'day')) {
    days.push(cursor.toDate());
    cursor = cursor.add(1, 'day');
  }

  return days;
};

/**
 * Number of full calendar days between the two dates (time-of-day ignored).
 * Mirrors date-fns `differenceInCalendarDays(dateLeft, dateRight)`.
 *
 * We diff in fractional days and round: across a DST boundary a calendar day
 * is only 23h (or 25h), so an integer `.diff(…, 'day')` would floor 0.96 to 0.
 * Rounding the float keeps the count correct in local (non-UTC) timezones.
 */
export const differenceInCalendarDays = (
  dateLeft: DateInput,
  dateRight: DateInput
): number =>
  Math.round(
    dayjs(dateLeft)
      .startOf('day')
      .diff(dayjs(dateRight).startOf('day'), 'day', true)
  );

/**
 * Number of whole hours between the two dates.
 * Mirrors date-fns `differenceInHours(dateLeft, dateRight)`.
 */
export const differenceInHours = (
  dateLeft: DateInput,
  dateRight: DateInput
): number => dayjs(dateLeft).diff(dayjs(dateRight), 'hour');

/**
 * Number of business days (Mon–Fri) between the two dates, counting the
 * half-open range `[min, max)` — start inclusive, end exclusive. Sign follows
 * `dateLeft - dateRight`. Returns `NaN` for an unparseable date.
 *
 * Matches date-fns `differenceInBusinessDays` exactly for `dateLeft >= dateRight`
 * (verified over every pair in a multi-year span). Unlike date-fns — whose
 * "whole weeks" shortcut makes it non-antisymmetric — this stays antisymmetric:
 * `f(a, b) === -f(b, a)`.
 */
export const differenceInBusinessDays = (
  dateLeft: DateInput,
  dateRight: DateInput
): number => {
  const left = dayjs(dateLeft).startOf('day');
  const right = dayjs(dateRight).startOf('day');

  if (!left.isValid() || !right.isValid()) {
    return NaN;
  }

  const sign = left.isBefore(right) ? -1 : 1;
  const earlier = left.isBefore(right) ? left : right;
  const later = left.isBefore(right) ? right : left;

  let count = 0;
  let cursor = earlier;

  while (cursor.isBefore(later, 'day')) {
    const weekDay = cursor.day();

    if (weekDay !== 0 && weekDay !== 6) {
      count += 1;
    }

    cursor = cursor.add(1, 'day');
  }

  return sign * count;
};

/** date-fns `format(date, token)` — `token` must be a dayjs format string. */
export const format = (date: DateInput, token: string): string =>
  dayjs(date).format(token);

/** date-fns `isBefore(date, dateToCompare)`. */
export const isBefore = (date: DateInput, dateToCompare: DateInput): boolean =>
  dayjs(date).isBefore(dayjs(dateToCompare));

/** date-fns `isAfter(date, dateToCompare)`. */
export const isAfter = (date: DateInput, dateToCompare: DateInput): boolean =>
  dayjs(date).isAfter(dayjs(dateToCompare));

export const isSameDay = (dateLeft: DateInput, dateRight: DateInput): boolean =>
  dayjs(dateLeft).isSame(dayjs(dateRight), 'day');

export const isToday = (date: DateInput): boolean =>
  dayjs(date).isSame(dayjs(), 'day');

export const isPast = (date: DateInput): boolean =>
  dayjs(date).isBefore(dayjs());

export const isFuture = (date: DateInput): boolean =>
  dayjs(date).isAfter(dayjs());

/** date-fns `getDay`: 0 = Sunday … 6 = Saturday. */
export const isSunday = (date: DateInput): boolean => dayjs(date).day() === 0;

/**
 * Inclusive on both bounds, mirroring date-fns `isWithinInterval`, except an
 * inverted interval (start after end) simply yields `false` here instead of
 * throwing a RangeError — a deliberate, friendlier contract.
 */
export const isWithinInterval = (
  date: DateInput,
  { start, end }: { start: DateInput; end: DateInput }
): boolean => dayjs(date).isBetween(dayjs(start), dayjs(end), null, '[]');

// The date-returning helpers below mirror date-fns and return a native `Date`
// (dayjs is only the calculation engine). The codebase interchanges dates as
// `Date`; callers that need a Dayjs simply wrap the result in `dayjs(...)`.
export const startOfDay = (date: DateInput): Date =>
  dayjs(date).startOf('day').toDate();

export const endOfDay = (date: DateInput): Date =>
  dayjs(date).endOf('day').toDate();

export const startOfISOWeek = (date: DateInput): Date =>
  dayjs(date).startOf('isoWeek').toDate();

export const endOfISOWeek = (date: DateInput): Date =>
  dayjs(date).endOf('isoWeek').toDate();

export const addDays = (date: DateInput, amount: number): Date =>
  dayjs(date).add(amount, 'day').toDate();

export const subDays = (date: DateInput, amount: number): Date =>
  dayjs(date).subtract(amount, 'day').toDate();

export const addMonths = (date: DateInput, amount: number): Date =>
  dayjs(date).add(amount, 'month').toDate();

export const subMonths = (date: DateInput, amount: number): Date =>
  dayjs(date).subtract(amount, 'month').toDate();

export const addYears = (date: DateInput, amount: number): Date =>
  dayjs(date).add(amount, 'year').toDate();

export const subYears = (date: DateInput, amount: number): Date =>
  dayjs(date).subtract(amount, 'year').toDate();

export const addSeconds = (date: DateInput, amount: number): Date =>
  dayjs(date).add(amount, 'second').toDate();

export const addWeeks = (date: DateInput, amount: number): Date =>
  dayjs(date).add(amount, 'week').toDate();

export const subWeeks = (date: DateInput, amount: number): Date =>
  dayjs(date).subtract(amount, 'week').toDate();

// date-fns `weekStartsOn`: 0 = Sunday (the 'en' locale default), 1 = Monday
// (ISO week). We only map those two — the only values used in the codebase.
type WeekOptions = { weekStartsOn?: 0 | 1 };

const weekUnit = (options?: WeekOptions): 'week' | 'isoWeek' =>
  options?.weekStartsOn === 1 ? 'isoWeek' : 'week';

export const startOfWeek = (date: DateInput, options?: WeekOptions): Date =>
  dayjs(date).startOf(weekUnit(options)).toDate();

export const endOfWeek = (date: DateInput, options?: WeekOptions): Date =>
  dayjs(date).endOf(weekUnit(options)).toDate();

export const startOfMonth = (date: DateInput): Date =>
  dayjs(date).startOf('month').toDate();

export const endOfMonth = (date: DateInput): Date =>
  dayjs(date).endOf('month').toDate();

export const startOfYear = (date: DateInput): Date =>
  dayjs(date).startOf('year').toDate();

export const endOfYear = (date: DateInput): Date =>
  dayjs(date).endOf('year').toDate();

/** date-fns `add(date, duration)`. */
export const add = (
  date: DateInput,
  duration: {
    years?: number;
    months?: number;
    weeks?: number;
    days?: number;
    hours?: number;
    minutes?: number;
    seconds?: number;
  }
): Date =>
  dayjs(date)
    .add(duration.years ?? 0, 'year')
    .add(duration.months ?? 0, 'month')
    .add(duration.weeks ?? 0, 'week')
    .add(duration.days ?? 0, 'day')
    .add(duration.hours ?? 0, 'hour')
    .add(duration.minutes ?? 0, 'minute')
    .add(duration.seconds ?? 0, 'second')
    .toDate();

/** date-fns `isEqual`: same instant. */
export const isEqual = (dateLeft: DateInput, dateRight: DateInput): boolean =>
  dayjs(dateLeft).isSame(dayjs(dateRight));

/** date-fns `isSameISOWeek`: same ISO week (Mon–Sun). */
export const isSameISOWeek = (
  dateLeft: DateInput,
  dateRight: DateInput
): boolean => dayjs(dateLeft).isSame(dayjs(dateRight), 'isoWeek');

/**
 * date-fns `nextSunday`: the next Sunday strictly after `date`
 * (a Sunday returns the following Sunday, +7 days).
 */
export const nextSunday = (date: DateInput): Date => {
  const d = dayjs(date);
  const daysUntilSunday = (7 - d.day()) % 7 || 7;

  return d.add(daysUntilSunday, 'day').toDate();
};

/** date-fns `differenceInMonths`: whole calendar months between the dates. */
export const differenceInMonths = (
  dateLeft: DateInput,
  dateRight: DateInput
): number => dayjs(dateLeft).diff(dayjs(dateRight), 'month');

/** date-fns `differenceInWeeks`: whole weeks between the dates. */
export const differenceInWeeks = (
  dateLeft: DateInput,
  dateRight: DateInput
): number => dayjs(dateLeft).diff(dayjs(dateRight), 'week');

/** date-fns `differenceInYears`: whole years between the dates. */
export const differenceInYears = (
  dateLeft: DateInput,
  dateRight: DateInput
): number => dayjs(dateLeft).diff(dayjs(dateRight), 'year');

/**
 * date-fns `parse(value, format, referenceDate)`: parse `value` against a
 * dayjs format string. `referenceDate` is accepted for signature parity but
 * ignored — dayjs fills missing parts from the current day like date-fns does.
 */
export const parse = (
  value: string,
  token: string,
  _referenceDate?: DateInput
): Date => dayjs(value, token).toDate();

/** date-fns `getDay`: day of week, 0 = Sunday … 6 = Saturday. */
export const getDay = (date: DateInput): number => dayjs(date).day();

/** date-fns `getWeek`: week of year. `weekStartsOn: 1` → ISO week number. */
export const getWeek = (date: DateInput, options?: WeekOptions): number =>
  options?.weekStartsOn === 1 ? dayjs(date).isoWeek() : dayjs(date).week();

/** date-fns `setDay`: set day of week (0 = Sunday … 6 = Saturday). */
export const setDay = (date: DateInput, day: number): Date =>
  dayjs(date).day(day).toDate();

/** date-fns `parseISO`: parse an ISO-8601 string. */
export const parseISO = (value: string): Date => dayjs(value).toDate();
